import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const CategoryList = () => {
  const colors = useBrandToken();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const getBusinessCategory = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/business_category');
      const list = response?.data || [];
      setData(Array.isArray(list) ? list : []);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessCategory();
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await axios.post('/business_category', {
        name: values.name,
        images: [],
      });
      message.success('Category added');
      setOpen(false);
      form.resetFields();
      getBusinessCategory();
    } catch (e) {
      // validation or API
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (record) => {
    try {
      await axios.delete(`/business_category/${record._id}`);
      message.success('Category deleted');
      getBusinessCategory();
    } catch (e) {
      // interceptor
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: 'Images',
      key: 'images',
      render: (_, r) => (Array.isArray(r.images) ? r.images.length : 0),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Id',
      dataIndex: '_id',
      key: '_id',
      ellipsis: true,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="Delete this category?"
          onConfirm={() => deleteCategory(record)}
        >
          <Button danger type="link">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <PageShell
      title="Categories"
      description="Business categories shown across customer and merchant apps."
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={getBusinessCategory} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setOpen(true);
            }}
          >
            Add category
          </Button>
        </Space>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          dataSource={data}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Add category"
        open={open}
        onOk={handleOk}
        confirmLoading={saving}
        onCancel={() => setOpen(false)}
        okButtonProps={{ type: 'primary' }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Category name"
            name="name"
            rules={[{ required: true, message: 'Enter category name' }]}
          >
            <Input placeholder="e.g. Restaurant" />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default CategoryList;
